import React, { useEffect, useState } from 'react'
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap'

type ActivationType = 'full' | 'date'

interface ActivateUserModalProps {
  show: boolean
  username?: string
  hasTrialDate?: boolean
  onHide: () => void
  onSubmit: (activationType: ActivationType, expirationDate?: string) => Promise<void>
}

const ActivateUserModal: React.FC<ActivateUserModalProps> = ({ show, username, hasTrialDate, onHide, onSubmit }) => {
  const [activationType, setActivationType] = useState<ActivationType>('full')
  const [expirationDate, setExpirationDate] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return
    setActivationType('full')
    setExpirationDate('')
    setError(null)
    setLoading(false)
  }, [show])

  const handleClose = () => {
    if (loading) return
    onHide()
  }

  const handleSubmit = async () => {
    setError(null)

    if (activationType === 'date' && !expirationDate) {
      setError('Please select an expiration date.')
      return
    }

    setLoading(true)
    try {
      await onSubmit(activationType, activationType === 'date' ? expirationDate : undefined)
      setLoading(false)
      onHide()
    } catch (err: any) {
      setLoading(false)
      setError(err?.message || 'Activation failed. Please try again.')
    }
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Activate User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <p className="mb-3">
          Manage expiration settings for <strong>{username}</strong>.
        </p>

        {hasTrialDate && (
          <Alert variant="warning" className="py-2">
            This user currently has a trial expiration date.
          </Alert>
        )}

        <Form>
          <Form.Check
            type="radio"
            id="activate-full"
            name="activationType"
            label="Activate fully (no expiration)"
            checked={activationType === 'full'}
            onChange={() => setActivationType('full')}
            disabled={loading}
            className="mb-2"
          />

          <Form.Check
            type="radio"
            id="activate-date"
            name="activationType"
            label="Set a specific expiration date"
            checked={activationType === 'date'}
            onChange={() => setActivationType('date')}
            disabled={loading}
            className="mb-2"
          />

          {activationType === 'date' && (
            <Form.Group className="mt-2" controlId="expirationDate">
              <Form.Label>Expiration date</Form.Label>
              <Form.Control
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                disabled={loading}
              />
              <Form.Text className="text-muted">The user will expire at the end of this date.</Form.Text>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="success" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{' '}
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ActivateUserModal
